import React, { useMemo, useState } from "react";

import useGetPortfolioPlanById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import useGetPortfolioPlanAssignments from "@fartherfinance/frontend/api/Investment/hooks/usePortfolioPlanAssignments";
import {
  PortfolioPlanAssignmentId,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import CustomDropdownBase from "@src/sharedComponents/CustomDropdownBase/CustomDropdownBase";
import DropdownMenuOptions from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";

export type PortfolioPlanUnion = PortfolioPlan | Assignment;

type PortfolioPlan = {
  id: PortfolioPlanId;
  type: "PortfolioPlan";
  label: string;
};

export type Assignment = {
  id: PortfolioPlanAssignmentId;
  type: "PortfolioPlanAssignment";
  label: string;
};

type PortfolioAssignmentsDropDownProps = {
  portfolioPlanId: PortfolioPlanId;
  curItem?: string;
  onSelect: (el: PortfolioPlanUnion) => void;
};

const AssignmentsDropDown = ({
  portfolioPlanId,
  curItem,
  onSelect,
}: PortfolioAssignmentsDropDownProps) => {
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const clientAuth = useRequestAuth();

  const portfolioAssignments = useGetPortfolioPlanAssignments(
    {
      abstractPortfolioId: portfolioPlanId,
      queryString: { accountOwnedBy: clientAuth?.clientId },
    },
    clientAuth
  );

  const portfolioData = useGetPortfolioPlanById(
    portfolioPlanId,
    {},
    clientAuth
  );

  const options = useMemo(() => {
    if (
      portfolioData.data === undefined ||
      portfolioAssignments.data === undefined
    ) {
      return [];
    }

    const portfolioAndAssignmentOptions: PortfolioPlanUnion[] = [
      {
        id: portfolioData.data.data.plan.portfolioPlanId,
        label: portfolioData.data.data.plan.name,
        type: "PortfolioPlan",
      },
      ...portfolioAssignments.data.data.map((assignment) => {
        return {
          id: assignment.planAssignmentId,
          label: assignment.name,
          type: "PortfolioPlanAssignment",
        } as const;
      }),
    ];

    return portfolioAndAssignmentOptions;
  }, [portfolioAssignments.data, portfolioData.data]);

  return (
    <CustomDropdownBase
      curItem={curItem ? { value: curItem } : { value: options[0]?.label }}
      showDropdown={showDropdown}
      setShowDropdown={setShowDropdown}
      isLoading={portfolioAssignments.isLoading || portfolioData.isLoading}
      isDisabled={portfolioAssignments.isLoading || portfolioData.isLoading}
      width={200}
      placeholder="Select Portfolio Assignment"
    >
      <DropdownMenuOptions
        onSelect={(el) => {
          onSelect(el);
          setShowDropdown(false);
        }}
        options={options}
      />
    </CustomDropdownBase>
  );
};

export default AssignmentsDropDown;
